"use client"

import { createContext, useContext, useState, useCallback, type ReactNode } from "react"
import { searchApi } from "@/api/search"
import type { Filters } from "@/components/search/FilterModal"
import { 
  SuggestAndFetchHistoryResponse,
  ProcessedCandidateInBestFitCandidate,
} from "@/types/search" 

export interface CandidateAttributes {
  id: number
  name: string
  email: string
  phone: string
  countryCode: string
  profilePictureURL?: string
  employmentStatus: string
  interestedRoles: string[]
  employmentType: string
  yearsOfExperience: number
  salaryExpectation: {
    currency: string
    min: number
    max: number
  }
  currentSalary?: {
    currency: "USD" | "INR" | "EUR" | "GBP"
    value: number
  }
  resumeDetails: {
    contact_information: {
      name: string
      email: string
      phone: string
      linkedin: string
      github: string
      website: string
    }
    summary: string
    skills: string[]
    projects: Array<{
      name: string
      description: string[]
      technologies_used: string[]
      start_date: string
      end_date: string
    }>
    experience: Array<{
      position: string
      company: string
      description: string[]
      start_date: string
      end_date: string
      location: string
      skills: string[]
    }>
    education: Array<{
      degree: string
      institution: string
      start_date: string
      end_date: string
      location: string
      skills: string[]
    }>
    resumeUrl?: string
    interests?: string[]
    backgroundInterviewQuestions?: string[]
  }
  matchPercentage: number
}

interface CandidatesState {
  data: ProcessedCandidateInBestFitCandidate[] | null;
  isLoading: boolean;
  error: Error | null;
}

interface SearchContextType {
  searchQuery: string
  setSearchQuery: (query: string) => void
  currentFilters: Filters
  setCurrentFilters: (filters: Filters) => void
  candidates: CandidatesState
  searchCandidates: (query: string, filters?: Filters, page?: number) => Promise<void>
  clearSearchHistory: () => void
  responseFilters: Filters | null
  defaultCandidatesList: SuggestAndFetchHistoryResponse['default']
  searchHistory: SuggestAndFetchHistoryResponse['history']
  initialDataLoading: boolean
  initialDataError: Error | null
  fetchInitialData: () => Promise<void>
  isReanalyzing: boolean
  reanalyzeInterview: (interviewId: string) => Promise<void>
}

const SearchContext = createContext<SearchContextType>({
  searchQuery: "",
  setSearchQuery: () => {},
  currentFilters: { candidate: {} },
  setCurrentFilters: () => {},
  candidates: {
    data: null,
    isLoading: false,
    error: null,
  },
  searchCandidates: async () => {},
  clearSearchHistory: () => {},
  responseFilters: null,
  defaultCandidatesList: [],
  searchHistory: [],
  initialDataLoading: false,
  initialDataError: null,
  fetchInitialData: async () => {},
  isReanalyzing: false,
  reanalyzeInterview: async () => {}
})

export function SearchProvider({ children }: { children: ReactNode }) {
  const [searchQuery, setSearchQuery] = useState("")
  const [currentFilters, setCurrentFilters] = useState<Filters>({ candidate: {} })
  const [responseFilters, setResponseFilters] = useState<Filters | null>(null)
  const [isReanalyzing, setIsReanalyzing] = useState<boolean>(false)
  const [candidates, setCandidates] = useState<CandidatesState>({
    data: null,
    isLoading: false,
    error: null,
  })

  const [defaultCandidatesList, setDefaultCandidatesList] = useState<SuggestAndFetchHistoryResponse['default']>([])
  const [searchHistory, setSearchHistory] = useState<SuggestAndFetchHistoryResponse['history']>([])
  const [initialDataLoading, setInitialDataLoading] = useState<boolean>(false)
  const [initialDataError, setInitialDataError] = useState<Error | null>(null)
  
  const reanalyzeInterview = useCallback(async (interviewId: string) => {
    if (!interviewId) return;
    
    setIsReanalyzing(true);
    try {
      await searchApi.reanalyzeInterview(interviewId);
      // Optionally, you could add a success notification here
      console.log('Interview reanalysis started successfully');
    } catch (error) {
      console.error('Failed to reanalyze interview:', error);
      // Optionally, you could add an error notification here
    } finally {
      setIsReanalyzing(false);
    }
  }, []);

  const clearSearchHistory = useCallback(() => {
    setSearchHistory([])
  }, [])

  const fetchInitialData = useCallback(async () => {
    setInitialDataLoading(true);
    setInitialDataError(null);
    try {
      const data = await searchApi.suggestCandidatesAndFetchHistory();
      setDefaultCandidatesList(data.default || []);
      setSearchHistory(data.history || []);
    } catch (error) {
      console.error('Failed to fetch initial search data:', error);
      setInitialDataError(error instanceof Error ? error : new Error('Failed to fetch initial data'));
      setDefaultCandidatesList([]);
      setSearchHistory([]);
    } finally {
      setInitialDataLoading(false);
    }
  }, []);

  const searchCandidatesHandler = useCallback(async (query: string, filters?: Filters) => {
    if (!query.trim() && (!filters || Object.keys(filters.candidate || {}).length === 0)) {
      setCandidates({ data: null, isLoading: false, error: null });
      return;
    }

    setCandidates((prev) => ({ ...prev, isLoading: true, error: null, data: null }));
    
    try {
      if (query.trim() && !searchHistory.some(item => item.title === query)) {
        setSearchHistory((prev) => [...prev, { id: Date.now(), title: query }].slice(0, 10));
      }
      const filtersToUse = filters || currentFilters;
      if (filters) setCurrentFilters(filters);

      const response = await searchApi.searchCandidates(query, filtersToUse);

      if ('filters' in response && response.filters) {
        setResponseFilters(response.filters);
      } else {
        setResponseFilters(filtersToUse);
      }

      setCandidates({
        data: response.data || [],
        isLoading: false,
        error: null,
      });
    } catch (error) {
      console.error('Search error:', error);
      setCandidates({ data: null, isLoading: false, error: error instanceof Error ? error : new Error("Unknown error occurred") });
    }
  }, [searchHistory, currentFilters]);

  return (
    <SearchContext.Provider
      value={{
        candidates,
        searchQuery,
        currentFilters,
        setCurrentFilters,
        setSearchQuery,
        clearSearchHistory,
        searchCandidates: searchCandidatesHandler,
        responseFilters,
        defaultCandidatesList,
        searchHistory,
        initialDataLoading,
        initialDataError,
        fetchInitialData,
        isReanalyzing,
        reanalyzeInterview
      }}
    >
      {children}
    </SearchContext.Provider>
  )
}

export function useSearch() {
  return useContext(SearchContext)
}